import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Calendar, Tag, ChevronLeft, User, Newspaper } from "lucide-react";
import DOMPurify from "dompurify";
import { normalizeImgUrl, normalizeHtmlContent } from "@/lib/utils-img";
import ShareButtons from "@/components/ShareButtons";
import SEO, { SITE_URL } from "@/components/SEO";
import Reveal from "@/components/motion/Reveal";
import Section from "@/components/ui/Section";
import Card, { CardBody } from "@/components/ui/Card";
import CTAButton, { ArrowLink } from "@/components/ui/CTAButton";
import SmartImage from "@/components/ui/SmartImage";
import GaleriaFotos from "@/components/GaleriaFotos";
import EmptyState from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet } from "@/lib/api";

// El navbar es fijo: h-16 en móvil, h-20 en escritorio — y ahora, encima,
// la cinta promocional fija (~37px) que vive en el mismo bloque fijo que
// <Navbar>. pt-16/pt-20 dejaban de bastar en cuanto se añadió la cinta.
const CLEARANCE = "pt-28 md:pt-32";

const CAT_LABELS = {
  delegacion: "Noticias de la Delegación",
  diocesis: "Diócesis de Huelva",
  "iglesia-espana": "Iglesia en España",
  blog: "Blog",
  recursos: "Recursos",
};

export default function NoticiaDetalle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setNotFound(false);
    setArticle(null);

    apiGet(`/articles/${slug}`)
      .then(data => {
        if (cancelado) return data;
        setArticle(data);
        return apiGet(`/articles?category=${data.category}&per_page=4`);
      })
      .then(data => {
        if (cancelado || !data?.articles) return;
        setRelated(data.articles.filter(a => a.slug !== slug).slice(0, 3));
      })
      .catch(err => {
        if (!cancelado && err.response?.status === 404) setNotFound(true);
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });

    return () => { cancelado = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className={`${CLEARANCE} min-h-screen`}>
        <Skeleton className="w-full aspect-[21/9] rounded-none" />
        <div className="max-w-3xl mx-auto px-6 md:px-8 py-12 space-y-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-3/4" />
          <div className="pt-6 space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className={`${CLEARANCE} min-h-screen`}>
        <SEO title="Artículo no encontrado" noIndex />
        <EmptyState
          icon={Newspaper}
          title="Artículo no encontrado"
          description="Puede que se haya retirado o que el enlace esté mal escrito."
          action={
            <CTAButton to="/noticias" className="font-semibold">
              <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Volver a Noticias
            </CTAButton>
          }
          className="py-32"
        />
      </div>
    );
  }

  const date = article.published_date
    ? new Date(article.published_date).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })
    : "";

  const ogImage = article.image ? normalizeImgUrl(article.image) : undefined;
  const ogUrl = `${SITE_URL}/noticias/${article.slug}`;
  const plainDescription = article.content
    ? article.content.replace(/<[^>]+>/g, "").substring(0, 160) + "…"
    : "Noticias de la Delegación de Evangelización Digital de la Diócesis de Huelva.";

  return (
    <div data-testid="noticia-detalle" className={`${CLEARANCE} min-h-screen`}>
      <SEO
        title={article.title}
        description={plainDescription}
        image={ogImage}
        canonical={ogUrl}
        type="article"
      />

      {/* Imagen de cabecera. Antes el contenedor llevaba `max-h-[480px]` sin
          altura definida, así que reservaba espacio cero y el artículo entero
          bajaba de golpe al decodificar la imagen. */}
      {article.image && (
        <SmartImage
          src={article.image}
          alt=""
          ratio="21 / 9"
          priority
          className="w-full max-h-[480px]"
        />
      )}

      <div className="max-w-3xl mx-auto px-6 md:px-8 py-12">
        <ArrowLink to="/noticias" data-testid="back-to-noticias" className="text-muted-foreground hover:text-primary mb-8">
          <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Volver a Noticias
        </ArrowLink>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 px-3 py-1.5 rounded-full">
            <Tag className="w-3 h-3" aria-hidden="true" />
            {CAT_LABELS[article.category] || article.category}
          </span>
          {date && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <Calendar className="w-3.5 h-3.5" aria-hidden="true" />
              <time dateTime={article.published_date}>{date}</time>
            </span>
          )}
          {article.author && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <User className="w-3.5 h-3.5" aria-hidden="true" />{article.author}
            </span>
          )}
        </div>

        <h1
          className="font-heading font-bold leading-tight mb-8 text-balance"
          style={{ fontSize: "clamp(30px, 5vw, 44px)", letterSpacing: "-0.03em" }}
        >
          {article.title}
        </h1>

        <div
          data-testid="article-content"
          className="article-content-container"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(normalizeHtmlContent(article.content) || "<p>Sin contenido.</p>"),
          }}
        />

        <GaleriaFotos fotos={article.gallery} />

        <div className="mt-10 pt-6 border-t border-border">
          <ShareButtons
            url={ogUrl}
            title={article.title}
            description={plainDescription}
            hashtags="EvangelizaciónDigital,Huelva"
          />
        </div>
      </div>

      {related.length > 0 && (
        <Section tone="muted" space="tight" className="border-t border-border">
          <h2 className="font-heading text-2xl font-bold mb-8">Noticias relacionadas</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((a, i) => (
              <Reveal key={a._id} delay={i * 70} distance={24}>
                <Card to={`/noticias/${a.slug}`} className="h-full">
                  <SmartImage
                    src={a.image}
                    alt=""
                    ratio="16 / 9"
                    fallback={
                      <div className="w-full h-full flex items-center justify-center bg-[#f5f5f7]">
                        <Newspaper className="w-8 h-8 text-foreground/15" aria-hidden="true" />
                      </div>
                    }
                  />
                  <CardBody className="p-4">
                    <p className="font-heading font-bold text-sm leading-snug group-hover:text-primary transition-colors duration-fast line-clamp-2">
                      {a.title}
                    </p>
                  </CardBody>
                </Card>
              </Reveal>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
